import { motion } from "framer-motion";
import { Star, BadgeCheck } from "lucide-react";
import { accentText } from "@/lib/text";

const FALLBACK = [
  { name: "Olivia", text: "I honestly got emotional listening to it. Hearing our story turned into music was the most beautiful feeling — truly personal." },
  { name: "Meera", text: "Ordered one for my parents' anniversary. My father, who never cries, had to leave the room. Worth every rupee." },
  { name: "Carlos", text: "Wanted something different from the usual gifts. A personalized song was such a creative idea — unforgettable." },
  { name: "Aarav", text: "The lyrics had our inside jokes, the café where we met, even her nickname. She asked how they possibly knew." },
  { name: "Tom", text: "If you want a gift that stands out, this is it. Turning your own story into a song is genuinely special." },
  { name: "Pearl", text: "Music already carries emotion, but making it personal takes it somewhere else entirely. Words can't do this." },
];

const rotates = ["rotate-[-1.5deg]", "rotate-[1.5deg]", "rotate-[-1deg]", "rotate-[1deg]", "rotate-[-1.5deg]", "rotate-[1.5deg]"];

export default function Testimonials({ content }) {
  const reviews = content?.testimonials?.length ? content.testimonials : FALLBACK;
  return (
    <section id="reviews" className="py-20 sm:py-28 scroll-mt-24 bg-cardalt border-y-[3px] border-ink" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6 }} className="text-center">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-pink font-semibold mb-4">{content?.testimonials_eyebrow ?? "Verified reviews"}</p>
          <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight leading-[1.1]">
            {accentText(content?.testimonials_heading ?? "The reactions say it ~better than we can~", "text-grape")}
          </h2>
          <div className="mt-5 inline-flex items-center gap-2 rounded-full bg-white border-2 border-ink px-5 py-2 shadow-[3px_3px_0_#2D1A32]" data-testid="rating-summary">
            {[...Array(5)].map((_, i) => <Star key={i} size={15} className="text-sunny" fill="#FFC700" />)}
            <span className="font-head font-bold text-sm ml-1">{content?.rating_text ?? "4.9/5 · 1,200+ verified reviews"}</span>
          </div>
        </motion.div>

        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-7">
          {reviews.map((r, i) => (
            <motion.figure
              key={r.name + i}
              data-testid={`review-card-${i}`}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.55, delay: (i % 3) * 0.1, type: "spring", bounce: 0.3 }}
              className={`sticker-card rounded-3xl bg-white p-7 ${rotates[i % rotates.length]}`}
            >
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, j) => <Star key={j} size={14} className="text-sunny" fill="#FFC700" />)}
              </div>
              <blockquote className="text-sm text-ink font-medium leading-relaxed">"{r.text}"</blockquote>
              <figcaption className="mt-5 flex items-center gap-2">
                <span className="font-display font-semibold text-sm">{r.name}</span>
                <span className="inline-flex items-center gap-1 rounded-full bg-mint/30 border border-ink px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.15em]">
                  <BadgeCheck size={11} /> Verified buyer
                </span>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
}
